import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPercent,
    faSearch,
    faSpinner,
    faTimes,
    faChevronDown,
    faChevronUp,
    faCheckCircle,
    faUserSlash,
    faUndoAlt,
    faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import Api from '../../Api';
import { useModalDismiss } from '../../hooks/useModalDismiss';

const fmt = (n) => Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtQty = (n) => Number(n || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 });
const fmtDate = (d) => (d ? new Date(d.replace(' ', 'T')).toLocaleDateString('fr-FR') : '');

const STATUS = {
    held: { label: 'En cours (retenue)', cls: 'bg-yellow-100 text-yellow-800' },
    released: { label: 'Libérée — payée au prochain lot', cls: 'bg-green-100 text-green-700' },
    paid: { label: 'Payée', cls: 'bg-gray-100 text-gray-700' },
    forfeited: { label: 'Abandonnée', cls: 'bg-red-100 text-red-700' },
};

const ROW_STATUS = {
    held: 'Retenue',
    released: 'Libérée',
    paid: 'Payée',
    forfeited: 'Abandonnée',
};

const EVENT_LABELS = {
    withheld: 'Retenue appliquée (lot confirmé)',
    released: 'Travail terminé : garantie libérée',
    release_cancelled: 'Libération annulée',
    forfeited: 'Travail abandonné',
    forfeit_cancelled: 'Abandon annulé',
    paid: 'Garantie payée',
};

const ACTIONS = {
    release: {
        title: 'Travail terminé', button: 'Marquer terminé', cls: 'bg-green-600 hover:bg-green-700', icon: faCheckCircle,
        help: 'Toute la retenue en cours sur cette enquête devient payable : elle sera payée dans le prochain lot automatique (un lot déjà en brouillon doit être recalculé pour l\'inclure). Le quota affecté doit être entièrement réalisé.',
    },
    'cancel-release': {
        title: 'Annuler « Travail terminé »', button: 'Annuler la libération', cls: 'bg-gray-700 hover:bg-gray-800', icon: faUndoAlt,
        help: 'La garantie redevient retenue. Possible tant qu\'aucun lot ne l\'a reprise.',
    },
    forfeit: {
        title: 'Travail abandonné', button: 'Marquer abandonné', cls: 'bg-red-600 hover:bg-red-700', icon: faUserSlash,
        help: 'La retenue en cours sur cette enquête ne sera pas payée et n\'apparaîtra plus dans ses lignes de paiement. Réversible si le prestataire reprend le travail.',
    },
    'cancel-forfeit': {
        title: 'Annuler l\'abandon', button: 'Rétablir la retenue', cls: 'bg-yellow-600 hover:bg-yellow-700', icon: faUndoAlt,
        help: 'La retenue redevient « en cours » : elle pourra être libérée quand le travail sera terminé.',
    },
};

/**
 * "Retenues de garantie" tab of the unpaid page: guarantees per F2F supervisor × survey, their weekly
 * history, and the chef terrain's actions (Travail terminé / Abandon and their undo).
 */
const RetenueGuarantees = ({ navigate }) => {
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [status, setStatus] = useState('');
    const [groups, setGroups] = useState([]);
    const [stats, setStats] = useState(null);
    const [canAct, setCanAct] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [expanded, setExpanded] = useState({});

    const [actionTarget, setActionTarget] = useState(null); // { group, action }
    const [actionReason, setActionReason] = useState('');
    const [acting, setActing] = useState(false);
    const [actionError, setActionError] = useState(null); // { message, quota }
    const actionDismiss = useModalDismiss(() => setActionTarget(null), { backdropClose: false, enabled: !!actionTarget && !acting });
    const errorDismiss = useModalDismiss(() => setActionError(null), { enabled: !!actionError });

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 400);
        return () => clearTimeout(t);
    }, [search]);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const params = new URLSearchParams();
            if (debouncedSearch) params.append('search', debouncedSearch);
            if (status) params.append('status', status);
            const res = await Api.get(`/payments/unpaid/retenues?${params}`);
            setGroups(res.data?.data || []);
            setStats(res.data?.stats || null);
            setCanAct(!!res.data?.can_act);
            setLoaded(true);
        } catch (e) {
            setError(e.response?.data?.message || 'Erreur lors du chargement des retenues de garantie');
        } finally {
            setLoading(false);
        }
    }, [debouncedSearch, status]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const openAction = (group, action) => {
        setActionTarget({ group, action });
        setActionReason('');
    };

    const submitAction = async () => {
        if (!actionTarget || !actionReason.trim()) return;
        const { group, action } = actionTarget;
        try {
            setActing(true);
            const res = await Api.post(`/payments/unpaid/retenues/${group.supervisor_id}/${group.survey_id}/${action}`, { reason: actionReason.trim() });
            setNotice(res.data?.message || 'Action effectuée');
            setActionTarget(null);
            fetchData();
        } catch (e) {
            setActionTarget(null);
            setActionError({ message: e.response?.data?.message || 'Erreur lors de l\'action', quota: e.response?.data?.quota || null });
        } finally {
            setActing(false);
        }
    };

    const QuotaBadge = ({ quota }) => {
        if (!quota) return <span className="text-gray-400">—</span>;
        return (
            <div className="space-y-0.5">
                {quota.groups.map((g, i) => {
                    const ok = g.assigned > 0 && g.done >= g.assigned;
                    return (
                        <div key={i} className={`text-xs ${ok ? 'text-green-700' : 'text-red-600'}`}>
                            {g.label ? <span className="text-gray-500">{g.label} : </span> : null}
                            réalisé <strong>{g.done}</strong> / affecté <strong>{g.assigned}</strong>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
                <FontAwesomeIcon icon={faPercent} className="mr-2" />
                Retenues de garantie F2F : chaque semaine, le pourcentage retenu sur le travail d'une enquête est gardé ici.
                Il est payé en une fois quand le chef terrain marque le <strong>travail terminé</strong> (quota entièrement réalisé),
                ou n'est pas payé si le travail est <strong>abandonné</strong>.
            </div>

            {notice && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800 flex justify-between">
                    <span>{notice}</span>
                    <button onClick={() => setNotice('')}><FontAwesomeIcon icon={faTimes} /></button>
                </div>
            )}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 flex justify-between">
                    <span>{error}</span>
                    <button onClick={() => setError('')}><FontAwesomeIcon icon={faTimes} /></button>
                </div>
            )}

            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                        ['En cours (retenu)', stats.held, 'text-yellow-700'],
                        ['Libéré (à payer)', stats.released, 'text-green-700'],
                        ['Payé', stats.paid, 'text-gray-900'],
                        ['Abandonné', stats.forfeited, 'text-red-600'],
                    ].map(([label, value, cls]) => (
                        <div key={label} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                            <p className="text-sm text-gray-600">{label}</p>
                            <p className={`text-xl font-bold ${cls}`}>{fmt(value)} MAD</p>
                        </div>
                    ))}
                </div>
            )}

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex flex-col md:flex-row gap-3">
                <div className="relative flex-1">
                    <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Rechercher (prestataire, REF, enquête)..."
                        className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                    />
                    {loading && loaded && <FontAwesomeIcon icon={faSpinner} spin className="absolute right-3 top-1/2 -translate-y-1/2 text-yellow-500" />}
                </div>
                <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg">
                    <option value="">Tous les statuts</option>
                    {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
            </div>

            {loading && !loaded ? (
                <div className="p-8 text-center text-gray-500"><FontAwesomeIcon icon={faSpinner} spin className="mr-2" />Chargement...</div>
            ) : groups.length === 0 ? (
                <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">Aucune retenue de garantie.</div>
            ) : (
                <div className="space-y-3">
                    {groups.map((g) => {
                        const isOpen = !!expanded[g.key];
                        const st = STATUS[g.status] || STATUS.held;
                        return (
                            <div key={g.key} className="bg-white rounded-lg shadow-sm border border-gray-200">
                                <div className="p-4 flex flex-col lg:flex-row lg:items-center gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-gray-900">
                                            {g.supervisor_name}
                                            {g.supervisor_archived && <span className="ml-2 text-xs text-gray-400">(archivé)</span>}
                                        </div>
                                        <div className="text-xs text-gray-400">Ref: {g.supervisor_ref || 'N/A'}</div>
                                        <div className="text-sm text-gray-600 mt-1">{g.survey_title}</div>
                                    </div>
                                    <div className="text-sm min-w-[160px]">
                                        {g.held_amount > 0 && <div><span className="text-gray-500">Retenu :</span> <strong className="text-yellow-700">{fmt(g.held_amount)} MAD</strong> <span className="text-xs text-gray-400">({fmtQty(g.held_qty)} compl.)</span></div>}
                                        {g.released_amount > 0 && <div><span className="text-gray-500">Libéré :</span> <strong className="text-green-700">{fmt(g.released_amount)} MAD</strong></div>}
                                        {g.paid_amount > 0 && <div><span className="text-gray-500">Payé :</span> <strong>{fmt(g.paid_amount)} MAD</strong></div>}
                                        {g.forfeited_amount > 0 && <div><span className="text-gray-500">Abandonné :</span> <strong className="text-red-600">{fmt(g.forfeited_amount)} MAD</strong></div>}
                                    </div>
                                    <div className="min-w-[170px]">
                                        {g.held_amount > 0 && <QuotaBadge quota={g.quota} />}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${st.cls}`}>{st.label}</span>
                                        {canAct && g.held_amount > 0 && (
                                            <>
                                                <button onClick={() => openAction(g, 'release')} className="px-3 py-1.5 text-xs rounded-lg text-white bg-green-600 hover:bg-green-700">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />Travail terminé
                                                </button>
                                                <button onClick={() => openAction(g, 'forfeit')} className="px-3 py-1.5 text-xs rounded-lg text-red-700 border border-red-300 hover:bg-red-50">
                                                    <FontAwesomeIcon icon={faUserSlash} className="mr-1" />Abandon
                                                </button>
                                            </>
                                        )}
                                        {canAct && g.released_amount > 0 && (
                                            <button onClick={() => openAction(g, 'cancel-release')} className="px-3 py-1.5 text-xs rounded-lg text-gray-700 border border-gray-300 hover:bg-gray-50">
                                                <FontAwesomeIcon icon={faUndoAlt} className="mr-1" />Annuler « terminé »
                                            </button>
                                        )}
                                        {canAct && g.forfeited_amount > 0 && (
                                            <button onClick={() => openAction(g, 'cancel-forfeit')} className="px-3 py-1.5 text-xs rounded-lg text-yellow-700 border border-yellow-300 hover:bg-yellow-50">
                                                <FontAwesomeIcon icon={faUndoAlt} className="mr-1" />Annuler l'abandon
                                            </button>
                                        )}
                                        <button onClick={() => setExpanded(p => ({ ...p, [g.key]: !p[g.key] }))} className="text-gray-400 hover:text-gray-600 px-2" title="Détail">
                                            <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
                                        </button>
                                    </div>
                                </div>
                                {isOpen && (
                                    <div className="border-t border-gray-100 p-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                                        <div>
                                            <h4 className="text-sm font-medium text-gray-700 mb-2">Retenues par semaine</h4>
                                            <div className="overflow-x-auto">
                                                <table className="min-w-full text-xs">
                                                    <thead className="text-gray-500">
                                                        <tr><th className="text-left py-1 pr-3">Lot</th><th className="text-left pr-3">Vague</th><th className="text-right pr-3">%</th><th className="text-right pr-3">Base</th><th className="text-right pr-3">Retenu</th><th className="text-right pr-3">Compl.</th><th className="text-left">Statut</th></tr>
                                                    </thead>
                                                    <tbody>
                                                        {g.weeks.map((w) => (
                                                            <tr key={w.id} className="border-t border-gray-100">
                                                                <td className="py-1 pr-3">
                                                                    <button onClick={() => navigate(`/payments/${w.batch_id}`)} className="text-blue-600 hover:underline">#{w.batch_id}</button>
                                                                </td>
                                                                <td className="pr-3">{w.waves?.length ? w.waves.join(', ') : '—'}</td>
                                                                <td className="text-right pr-3">{fmtQty(w.percentage)} %</td>
                                                                <td className="text-right pr-3">{fmt(w.base_amount)}</td>
                                                                <td className="text-right pr-3 font-medium">{fmt(w.amount)}</td>
                                                                <td className="text-right pr-3">{fmtQty(w.qty)}</td>
                                                                <td>{ROW_STATUS[w.status] || w.status}{w.paid_in_batch_id ? <> (lot <button onClick={() => navigate(`/payments/${w.paid_in_batch_id}`)} className="text-blue-600 hover:underline">#{w.paid_in_batch_id}</button>)</> : null}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                        <div>
                                            <h4 className="text-sm font-medium text-gray-700 mb-2">Historique</h4>
                                            <div className="space-y-1.5">
                                                {g.events.length === 0 ? <div className="text-xs text-gray-400">—</div> : g.events.map((e, i) => (
                                                    <div key={i} className="text-xs text-gray-700">
                                                        <span className="text-gray-400">{fmtDate(e.created_at)}</span>{' '}
                                                        <strong>{EVENT_LABELS[e.event] || e.event}</strong>
                                                        {e.amount != null && <> · {fmt(e.amount)} MAD</>}
                                                        {e.batch_id && <> · lot #{e.batch_id}</>}
                                                        {e.user_name && <> · {e.user_name}</>}
                                                        {e.reason && <div className="text-gray-500 ml-4">« {e.reason} »</div>}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Action modal (reason required) */}
            {actionTarget && (() => {
                const a = ACTIONS[actionTarget.action];
                const g = actionTarget.group;
                const amount = actionTarget.action === 'cancel-release' ? g.released_amount : actionTarget.action === 'cancel-forfeit' ? g.forfeited_amount : g.held_amount;
                return (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" {...actionDismiss}>
                        <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
                            <div className="flex items-center justify-between p-6 border-b border-gray-200">
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900"><FontAwesomeIcon icon={a.icon} className="mr-2" />{a.title}</h3>
                                    <p className="text-sm text-gray-600">{g.supervisor_name} · {g.survey_title} · {fmt(amount)} MAD</p>
                                </div>
                                <button onClick={() => setActionTarget(null)} className="text-gray-400 hover:text-gray-600" disabled={acting}>
                                    <FontAwesomeIcon icon={faTimes} className="text-xl" />
                                </button>
                            </div>
                            <div className="p-6 space-y-4">
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-700">{a.help}</div>
                                {actionTarget.action === 'release' && g.quota && (
                                    <div className="text-sm"><span className="text-gray-600">Quota :</span> <QuotaBadge quota={g.quota} /></div>
                                )}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Motif <span className="text-red-500">*</span></label>
                                    <textarea
                                        value={actionReason}
                                        onChange={(e) => setActionReason(e.target.value)}
                                        rows="3"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
                                <button onClick={() => setActionTarget(null)} disabled={acting} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Annuler</button>
                                <button onClick={submitAction} disabled={acting || !actionReason.trim()} className={`${a.cls} text-white px-6 py-2 rounded-lg disabled:opacity-50`}>
                                    {acting ? <><FontAwesomeIcon icon={faSpinner} spin className="mr-2" />...</> : a.button}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Refusal modal (e.g. quota not completed) */}
            {actionError && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" {...errorDismiss}>
                    <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
                        <div className="flex items-center justify-between p-6 border-b border-red-200 bg-red-50 rounded-t-xl">
                            <h3 className="text-lg font-bold text-red-800"><FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />Action impossible</h3>
                            <button onClick={() => setActionError(null)} className="text-gray-400 hover:text-gray-600">
                                <FontAwesomeIcon icon={faTimes} className="text-xl" />
                            </button>
                        </div>
                        <div className="p-6 space-y-3 text-sm text-gray-800">
                            <p>{actionError.message}</p>
                            {actionError.quota?.groups?.length > 0 && (
                                <table className="min-w-full text-sm">
                                    <thead className="text-gray-500"><tr><th className="text-left py-1">Vague</th><th className="text-right">Affecté</th><th className="text-right">Réalisé</th></tr></thead>
                                    <tbody>
                                        {actionError.quota.groups.map((q, i) => (
                                            <tr key={i} className={`border-t border-gray-100 ${q.assigned > 0 && q.done >= q.assigned ? 'text-green-700' : 'text-red-600'}`}>
                                                <td className="py-1">{q.label || 'Enquête'}</td>
                                                <td className="text-right">{q.assigned}</td>
                                                <td className="text-right">{q.done}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                        <div className="flex justify-end p-6 border-t border-gray-200">
                            <button onClick={() => setActionError(null)} className="px-6 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900">Fermer</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RetenueGuarantees;
